import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import { classnames } from 'tailwindcss-classnames';
import { TransplantTotals } from './_types';

const styles = {
  border: classnames(`border-solid`),
};

const TransplantsTable = (totals: TransplantTotals): React.JSX.Element => {
  // Calculated:
  const allogeneicMarrowTotal: number = ((obj) => obj.relatedMarrow + obj.unrelatedMarrow)(totals);
  const allogeneicPbscTotal: number = ((obj) => obj.relatedPbsc + obj.unrelatedPbsc)(totals);
  const allogeneicCordBloodTotal: number = ((obj) => obj.relatedCordBlood + obj.unrelatedCordBlood)(
    totals
  );
  const marrowAndPbscTotal: number = ((obj) =>
    allogeneicMarrowTotal + allogeneicPbscTotal + obj.autologous)(totals); // This should also equal totals.grandTotal - totals.cordBlood

  const tableCss = classnames('border-2', styles.border, 'border-gray-whisper');
  const tableTrCss = classnames(
    'even:bg-gray-whisper',
    'text-solidBlack',
    'border',
    'sm:border-none',
    styles.border,
    'border-gray-quartz'
  );
  const tableThCss = classnames(
    'p-0',
    'sm:p-3',
    'sm:border',
    'text-left',
    styles.border,
    'border-gray-quartz'
  );
  const tableTdDataCss = classnames(
    'p-0',
    'sm:p-3',
    'sm:border',
    styles.border,
    'border-gray-quartz'
  );
  const tableTdTotalsCss = classnames(
    'p-0',
    'sm:p-3',
    'sm:border',
    styles.border,
    'border-gray-quartz',
    'pb-5'
  );
  const tableTdWideLabelCss = classnames(
    'ml-3',
    'sm:ml-0',
    'p-0',
    'sm:p-3',
    'sm:border',
    styles.border,
    'border-gray-quartz',
    'py-5'
  );
  const tableTdLabelCss = classnames(
    'm-0',
    'p-0',
    'sm:p-3',
    'sm:border',
    styles.border,
    'border-gray-quartz',
    'pt-5'
  );
  const spacerCss = classnames('inline-block', 'sm:hidden', 'w-1/2', 'mr-1');

  return (
    <>
      <Table className={tableCss}>
        <Thead>
          <Tr className={tableTrCss}>
            <Th className={tableThCss}>&nbsp;</Th>
            <Th className={tableThCss}>
              <b>Marrow</b>
            </Th>
            <Th className={tableThCss}>
              <b>PBSC</b>
            </Th>
            <Th className={tableThCss}>
              <b>Cord blood</b>
            </Th>
            <Th className={tableThCss}>
              <b>Total</b>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr className={tableTrCss}>
            <Td className={tableTdWideLabelCss} colSpan="5">
              <div className={spacerCss}>&nbsp;</div>
              <b>Allogeneic</b>
            </Td>
          </Tr>
          <Tr className={tableTrCss}>
            <Td className={tableTdLabelCss}>Related donor</Td>
            <Td className={tableTdDataCss}>{((obj) => obj.relatedMarrow)(totals)}</Td>
            <Td className={tableTdDataCss}>{((obj) => obj.relatedPbsc)(totals)}</Td>
            <Td className={tableTdDataCss}>{((obj) => obj.relatedCordBlood)(totals)}</Td>
            <Td className={tableTdTotalsCss}>{((obj) => obj.related)(totals)}</Td>
          </Tr>
          <Tr className={tableTrCss}>
            <Td className={tableTdLabelCss}>Unrelated donor</Td>
            <Td className={tableTdDataCss}>{((obj) => obj.unrelatedMarrow)(totals)}</Td>
            <Td className={tableTdDataCss}>{((obj) => obj.unrelatedPbsc)(totals)}</Td>
            <Td className={tableTdDataCss}>{((obj) => obj.unrelatedCordBlood)(totals)}</Td>
            <Td className={tableTdTotalsCss}>{((obj) => obj.unrelated)(totals)}</Td>
          </Tr>
          <Tr className={tableTrCss}>
            <Td className={tableTdLabelCss}>Total allogeneic</Td>
            <Td className={tableTdDataCss}>{allogeneicMarrowTotal}</Td>
            <Td className={tableTdDataCss}>{allogeneicPbscTotal}</Td>
            <Td className={tableTdDataCss}>{allogeneicCordBloodTotal}</Td>
            <Td className={tableTdTotalsCss}>{((obj) => obj.allogeneic)(totals)}</Td>
          </Tr>
          <Tr className={tableTrCss}>
            <Td className={tableTdLabelCss} colSpan="5">
              <div className={spacerCss}>&nbsp;</div>
              <b>Autologous</b>
            </Td>
          </Tr>
          <Tr className={tableTrCss}>
            <Td className={tableTdDataCss} />
            <Td className={tableTdDataCss}>
              Both marrow &amp; PBSC {((obj) => obj.autologous)(totals)}
            </Td>
            <Td className={tableTdDataCss}>—</Td>
            <Td className={tableTdDataCss}>—</Td>
            <Td className={tableTdTotalsCss}>{((obj) => obj.autologous)(totals)}</Td>
          </Tr>
          <Tr className={tableTrCss}>
            <Td className={tableTdLabelCss} colSpan="5">
              <div className={spacerCss}>&nbsp;</div>
              <b>Total combined</b>
            </Td>
          </Tr>
          <Tr className={tableTrCss}>
            <Td className={tableTdDataCss} />
            <Td className={tableTdDataCss}>Both marrow &amp; PBSC {marrowAndPbscTotal}</Td>
            <Td className={tableTdDataCss}>—</Td>
            <Td className={tableTdDataCss}>{((obj) => obj.cordBlood)(totals)}</Td>
            <Td className={tableTdTotalsCss}>{((obj) => obj.grandTotal)(totals)}</Td>
          </Tr>
        </Tbody>
      </Table>
    </>
  );
};

export default TransplantsTable;
